class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code !== 'Escape') return;
      if (this.suppressNextEscapeClose) {
        this.suppressNextEscapeClose = false;
        return;
      }
      this.close();
    });
    this.addEventListener('click', this.handleUpsellImageModalClick.bind(this));
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const sourceDialog = this.sourceDialog;
    this.sourceDialog = null;
    if (sourceDialog?.open) {
      if (typeof this.showPopover === 'function') {
        this.setAttribute('popover', 'manual');
        this.showPopover();
        const finishTransition = () => {
          if (this.finishSourceTransition !== finishTransition) return;
          this.finishSourceTransition = null;
          this.querySelector('.drawer__inner')?.removeEventListener('transitionend', finishTransition);
          sourceDialog.close();
          this.hidePopover();
          this.removeAttribute('popover');
        };
        this.finishSourceTransition = finishTransition;
        this.querySelector('.drawer__inner')?.addEventListener('transitionend', finishTransition);
        // Close the source dialog (e.g. the wig configurator) early instead of waiting for the full
        // --drawer-duration slide, so the page behind the drawer is usable right away.
        window.setTimeout(finishTransition, 250);
      } else {
        sourceDialog.close();
      }
    }
    document.documentElement.classList.add('cart-drawer-safari-open');
    document.dispatchEvent(new CustomEvent('zadeyra:media-overlay-open', { detail: { overlay: this } }));
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    }, 20);

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.finishSourceTransition?.();
    this.querySelectorAll('.cart-drawer-upsell__modal[open]').forEach((dialog) => dialog.close());
    const drawerInner = this.querySelector('.drawer__inner');
    const restoreDocumentBackground = () => {
      document.documentElement.classList.remove('cart-drawer-safari-open');
    };

    drawerInner?.addEventListener('transitionend', restoreDocumentBackground, { once: true });
    window.setTimeout(restoreDocumentBackground, 500);
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.dispatchEvent(new CustomEvent('zadeyra:media-overlay-close', { detail: { overlay: this } }));
  }

  handleUpsellImageModalClick(event) {
    const openTrigger = event.target.closest('[data-cart-upsell-modal-open]');
    if (openTrigger && this.contains(openTrigger)) {
      const modalId = openTrigger.getAttribute('aria-controls');
      const dialog = modalId ? this.querySelector(`#${CSS.escape(modalId)}`) : null;
      if (!dialog || typeof dialog.showModal !== 'function') return;

      dialog.addEventListener(
        'cancel',
        (cancelEvent) => {
          cancelEvent.preventDefault();
          this.suppressNextEscapeClose = true;
          dialog.close();
        },
        { once: true }
      );
      dialog.showModal();
      return;
    }

    const closeTrigger = event.target.closest('[data-cart-upsell-modal-close]');
    if (closeTrigger && this.contains(closeTrigger)) {
      closeTrigger.closest('dialog')?.close();
      return;
    }

    if (event.target.matches('.cart-drawer-upsell__modal')) event.target.close();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const preservedTrust = this.querySelector('.cart-drawer-trust');
    const preservedUpsellImage = this.querySelector('.cart-drawer-upsell__image-link');
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    const refreshedTrust = this.querySelector('.cart-drawer-trust');
    if (
      preservedTrust?.querySelector('.cart-drawer-trust__list') &&
      refreshedTrust?.querySelector('.cart-drawer-trust__list')
    ) {
      refreshedTrust.replaceWith(preservedTrust);
    }
    const refreshedUpsellImage = this.querySelector('.cart-drawer-upsell__image-link');
    if (
      preservedUpsellImage &&
      refreshedUpsellImage &&
      preservedUpsellImage.querySelector('img')?.getAttribute('src') ===
        refreshedUpsellImage.querySelector('img')?.getAttribute('src')
    ) {
      refreshedUpsellImage.replaceWith(preservedUpsellImage);
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const section = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (selector === '#CartDrawer') {
      const loadedImages = new Map();
      for (const image of document.images) {
        if (!image.complete || !image.naturalWidth) continue;
        const source = image.currentSrc || image.src;
        const key = this.imageCacheKey(source);
        if (key && !loadedImages.has(key)) loadedImages.set(key, source);
      }
      section.querySelectorAll('.cart-item__image').forEach((image) => {
        const source = loadedImages.get(this.imageCacheKey(image.getAttribute('src')));
        if (source) {
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.setAttribute('src', source);
        }
        image.setAttribute('loading', 'eager');
        image.setAttribute('fetchpriority', 'high');
      });
    }
    return section.innerHTML;
  }

  imageCacheKey(source) {
    if (!source) return null;
    try {
      const url = new URL(source, document.baseURI);
      if (!/^https?:$/.test(url.protocol)) return null;
      url.searchParams.delete('width');
      url.searchParams.sort();
      return url.href;
    } catch {
      return null;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
      {
        id: 'cart-inventory-status',
        selector: '#cart-inventory-status',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-inventory-status',
        section: 'cart-inventory-status',
        selector: '#cart-inventory-status',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
